import React, { useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import GroupsIcon from '@mui/icons-material/Groups';
import IosShareIcon from '@mui/icons-material/IosShare';
import { isCustomer } from '../../../api';
import { approveParticipant, blockParticipant, declineParticipant, removeParticipant, reportParticipant, toApiError } from '../api';
import { useHangoutInvite, useInviteParticipants } from '../hooks';
import { HangoutParticipant, InviteResponseStatus } from '../types';
import { errorTitle, loginUrlFor } from '../utils';
import { EmptyState, ErrorState, PageTop, SkeletonList } from '../components/common';
import { InviteParticipantRow, ParticipantActions } from '../components/InviteParticipantRow';
import { ShareInviteSheet } from '../components/ShareInviteSheet';
import '../hangouts.css';

type Tab = 'Going' | 'Interested' | 'Invited' | 'Requested';

function ReportDialog({ person, onClose, onSubmit }: { person: HangoutParticipant; onClose: () => void; onSubmit: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div className="hg-sheet-backdrop" onClick={onClose}>
      <form className="hg-sheet" role="dialog" aria-modal="true" aria-label={`Report ${person.displayName}`} onClick={(e) => e.stopPropagation()}
        onSubmit={async (e) => { e.preventDefault(); setBusy(true); await onSubmit(reason); setBusy(false); }}>
        <div className="hg-sheet-head"><h2>Report {person.displayName}</h2></div>
        <p className="hg-muted" style={{ marginTop: 0 }}>Tell us what happened. Our team reviews every report; {person.displayName} won't see who reported them.</p>
        <textarea className="hg-input" required maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What went wrong?" />
        <span className="hg-counter">{reason.length}/500</span>
        <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
          <button type="button" className="hg-btn hg-outline" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button type="submit" className="hg-btn hg-gold" style={{ flex: 1 }} disabled={busy || !reason.trim()}>{busy ? 'Sending…' : 'Send report'}</button>
        </div>
      </form>
    </div>
  );
}

// Screen 5 "Invite Responses" (/invites/:inviteId/responses). Hosts see everyone and manage
// requests; confirmed members see who's going. Only names and statuses are shown.
const InviteResponsesPage: React.FC = () => {
  const { inviteId } = useParams<{ inviteId: string }>();
  const id = Number(inviteId) || null;
  const location = useLocation();
  const signedIn = isCustomer();
  const invite = useHangoutInvite(signedIn ? id : null);
  const people = useInviteParticipants(signedIn ? id : null);
  const [tab, setTab] = useState<Tab>('Going');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [reporting, setReporting] = useState<HangoutParticipant | null>(null);
  const [sharing, setSharing] = useState(false);

  const list = useMemo(() => people.data ?? [], [people.data]);
  const byStatus = (s: InviteResponseStatus) => list.filter((p) => p.status === s && (s !== 'Going' || p.isApproved));
  const counts = { Going: byStatus('Going').length, Interested: byStatus('Interested').length, Invited: byStatus('Invited').length, Requested: byStatus('Requested').length };

  if (!signedIn) return <Navigate to={loginUrlFor(location.pathname)} replace />;

  const act = async (p: HangoutParticipant, fn: () => Promise<unknown>, done: string) => {
    setBusyId(p.participantId);
    setNotice(null);
    try {
      await fn();
      setNotice({ ok: true, text: done });
      await Promise.all([people.reload(), invite.reload()]);
    } catch (err) {
      setNotice({ ok: false, text: toApiError(err).message });
    } finally {
      setBusyId(null);
    }
  };

  const actions: ParticipantActions = {
    approve: (p) => act(p, () => approveParticipant(id!, p.participantId), `${p.displayName} can join now.`),
    decline: (p) => act(p, () => declineParticipant(id!, p.participantId), `Request from ${p.displayName} declined.`),
    remove: (p) => { if (window.confirm(`Remove ${p.displayName} from this hangout?`)) act(p, () => removeParticipant(id!, p.participantId), `${p.displayName} was removed.`); },
    block: (p) => {
      if (window.confirm(`Block ${p.displayName}? You won't see each other's messages or hangouts.`))
        act(p, () => blockParticipant(id!, p.participantId), `${p.displayName} is blocked.`);
    },
    report: (p) => setReporting(p)
  };

  const err = invite.error ?? people.error;
  if (err) {
    const e = errorTitle(err);
    return <main className="hg"><div className="hg-wrap"><PageTop title="Invite Responses" /><ErrorState title={e.title} body={e.body} /></div></main>;
  }

  const inv = invite.data;
  const isHost = !!inv?.isHost;
  const tabs: Tab[] = isHost ? ['Going', 'Interested', 'Invited', ...(counts.Requested > 0 || inv?.inviteType === 'PrivateGroup' ? ['Requested' as Tab] : [])] : ['Going', 'Interested'];
  const shown = byStatus(tab);
  const tabLabel = (t: Tab) => (t === 'Requested' ? `Requests (${counts.Requested})` : `${t} (${counts[t]})`);

  return (
    <main className="hg">
      <div className="hg-wrap">
        <PageTop title="Invite Responses" backTo={inv ? `/invite/${inv.publicToken}` : undefined} />
        {inv && <p className="hg-muted" style={{ textAlign: 'center', marginTop: -6 }}>{inv.venue.name} Night Out</p>}
        <div className="hg-tabs" role="tablist">
          {tabs.map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t} className={`hg-tab ${tab === t ? 'on' : ''}`} onClick={() => setTab(t)}>{tabLabel(t)}</button>
          ))}
        </div>
        {notice && <div className={`hg-notice ${notice.ok ? 'success' : 'error'}`} role="status">{notice.text}</div>}

        {people.loading && !people.data ? <SkeletonList rows={5} /> : shown.length === 0 ? (
          <EmptyState
            title={tab === 'Going' ? 'No one has joined yet' : tab === 'Requested' ? 'No pending requests' : `No one is ${tab.toLowerCase()} yet`}
            body={isHost ? 'Share your invite to get the night started.' : 'Check back closer to the night.'}
          />
        ) : (
          <div>
            {shown.map((p) => <InviteParticipantRow key={p.participantId} participant={p} isHost={isHost} busy={busyId === p.participantId} actions={actions} />)}
          </div>
        )}
      </div>

      {inv && (
        <div className="hg-sticky">
          <div className="hg-wrap">
            {inv.canChat && <Link className="hg-btn hg-gold block" to={`/invites/${inv.id}/chat`}><GroupsIcon />Open Group Chat</Link>}
            {inv.canShare && <button type="button" className="hg-btn hg-outline block" onClick={() => setSharing(true)}><IosShareIcon />Invite More People</button>}
          </div>
        </div>
      )}

      {sharing && inv && <ShareInviteSheet invite={inv} publicToken={inv.publicToken} refCode={inv.isHost ? null : inv.myRefCode} onClose={() => setSharing(false)} />}
      {reporting && id && (
        <ReportDialog person={reporting} onClose={() => setReporting(null)} onSubmit={async (reason) => {
          try {
            await reportParticipant(id, reporting.participantId, reason);
            setNotice({ ok: true, text: 'Thanks. Our team will review your report.' });
            setReporting(null);
          } catch (err) {
            setNotice({ ok: false, text: toApiError(err).message });
          }
        }} />
      )}
    </main>
  );
};

export default InviteResponsesPage;
